import React, { useState, useMemo } from 'react';
import {
  MessageSquare, Mail, Phone, ShoppingBag, AlertTriangle, QrCode, Voicemail,
  ChevronDown, ChevronRight, Calendar, Clock, AlertCircle, ArrowRight, ShieldCheck,
  Sparkles, BookOpen,
} from 'lucide-react';
import '../styles/PremiumScenario.css';

const CHANNEL_LABELS = {
  sms: 'SMS', email: 'Email', call: 'Phone call', voicemail: 'Voicemail',
  dm: 'Social DM', marketplace: 'Marketplace chat', whatsapp: 'WhatsApp',
  website: 'Website', qr: 'QR code', invoice: 'Invoice', multi: 'Multi-channel',
};
const CHANNEL_ICONS = {
  sms: MessageSquare, email: Mail, call: Phone, voicemail: Voicemail,
  dm: MessageSquare, marketplace: ShoppingBag, whatsapp: MessageSquare,
  website: AlertTriangle, qr: QrCode, invoice: Mail, multi: AlertCircle,
};
const CATEGORY_LABEL = {
  core: 'Core',
  family_emergency: 'Family emergency',
  banking_government: 'Banking & government',
  delivery_shopping: 'Delivery & shopping',
  marketplace_payid: 'Marketplace & PayID',
  account_security: 'Account security',
  emerging: 'Emerging pattern',
};

// ===================================================================
// Channel-specific renderers — give the scam content real visual surface
// ===================================================================
const SmsChannel = ({ sender, messages }) => (
  <div className="ps-sms" data-testid="ps-sms">
    <div className="ps-sms-bar">
      <div className="ps-sms-sender">{sender?.label || 'Unknown'}</div>
      {!sender?.verified && <span className="ps-sms-unverified">Unsaved number</span>}
    </div>
    <div className="ps-sms-thread">
      {messages.map((m, i) => (
        <div key={i} className={`ps-sms-row ps-sms-row-${m.from}`}>
          <div className={`ps-sms-bubble ps-sms-bubble-${m.from}`}>
            {m.text}
            {m.time && <span className="ps-sms-time">{m.time}</span>}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const EmailChannel = ({ sender, messages }) => (
  <div className="ps-email" data-testid="ps-email">
    <div className="ps-email-header">
      <div className="ps-email-row"><span className="ps-email-key">From</span><span className="ps-email-val">{sender?.label}</span></div>
      <div className="ps-email-row"><span className="ps-email-key">Subject</span><span className="ps-email-val">{messages[0]?.subject || 'Important — action required'}</span></div>
    </div>
    <div className="ps-email-body">
      {messages.map((m, i) => <p key={i}>{m.text}</p>)}
    </div>
  </div>
);

const CallChannel = ({ sender, transcript }) => (
  <div className="ps-call" data-testid="ps-call">
    <div className="ps-call-id">
      <Phone size={14} />
      <div>
        <div className="ps-call-from">{sender?.label || 'Unknown caller'}</div>
        <div className="ps-call-sub">Incoming · live transcript</div>
      </div>
    </div>
    <div className="ps-call-transcript">
      {(transcript || []).map((t, i) => (
        <div key={i} className="ps-call-line">
          <span className="ps-call-dot" />
          <p>"{t.text}"</p>
        </div>
      ))}
    </div>
  </div>
);

const MarketplaceChannel = ({ sender, messages }) => (
  <div className="ps-mkp" data-testid="ps-marketplace">
    <div className="ps-mkp-bar">
      <ShoppingBag size={13} />
      <div className="ps-mkp-meta">
        <div className="ps-mkp-name">{sender?.label}</div>
        <div className="ps-mkp-sub">Marketplace chat</div>
      </div>
    </div>
    <div className="ps-mkp-thread">
      {messages.map((m, i) => (
        <div key={i} className="ps-mkp-bubble">
          {m.text}
        </div>
      ))}
    </div>
  </div>
);

const DmChannel = ({ sender, messages }) => (
  <div className="ps-dm" data-testid="ps-dm">
    <div className="ps-dm-bar">
      <div className="ps-dm-avatar">{(sender?.label || '?').replace('@', '').slice(0, 1).toUpperCase()}</div>
      <div>
        <div className="ps-dm-name">{sender?.label}</div>
        <div className="ps-dm-sub">Direct message</div>
      </div>
    </div>
    <div className="ps-dm-thread">
      {messages.map((m, i) => (
        <div key={i} className="ps-dm-bubble">
          {m.text}
          {m.time && <span className="ps-dm-time">{m.time}</span>}
        </div>
      ))}
    </div>
  </div>
);

const QrChannel = ({ sender, messages }) => (
  <div className="ps-qr" data-testid="ps-qr">
    <div className="ps-qr-sticker">
      <div className="ps-qr-grid">
        {Array.from({ length: 49 }).map((_, i) => {
          // pseudo-random pattern, stable per index
          const filled = ((i * 13) % 7) > 2;
          return <span key={i} className={`ps-qr-cell ${filled ? 'on' : ''}`} />;
        })}
      </div>
      <div className="ps-qr-caption">Scan to pay</div>
    </div>
    <div className="ps-qr-side">
      <div className="ps-qr-where">{sender?.label}</div>
      {messages.map((m, i) => <p key={i} className="ps-qr-line">{m.text}</p>)}
    </div>
  </div>
);

const renderChannel = (channel, sender, messages, transcript) => {
  switch (channel) {
    case 'sms':
    case 'whatsapp':
      return <SmsChannel sender={sender} messages={messages || []} />;
    case 'email':
    case 'invoice':
      return <EmailChannel sender={sender} messages={messages || []} />;
    case 'call':
    case 'voicemail':
      return <CallChannel sender={sender} transcript={transcript || messages || []} />;
    case 'marketplace':
      return <MarketplaceChannel sender={sender} messages={messages || []} />;
    case 'dm':
      return <DmChannel sender={sender} messages={messages || []} />;
    case 'qr':
      return <QrChannel sender={sender} messages={messages || []} />;
    default:
      return <SmsChannel sender={sender} messages={messages || []} />;
  }
};

// ===================================================================
// Optional clues — collapsible "investigate the message" layer
// ===================================================================
const OptionalClues = ({ clues }) => {
  const [open, setOpen] = useState(false);
  if (!clues || !clues.length) return null;
  return (
    <div className="ps-clues" data-testid="ps-clues">
      <button className="ps-clues-toggle" onClick={() => setOpen(!open)} data-testid="ps-clues-toggle">
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span>Investigate the message</span>
        <span className="ps-clues-count">{clues.length} clues</span>
      </button>
      {open && (
        <div className="ps-clues-list">
          {clues.map((c, i) => (
            <div key={i} className="ps-clue">
              <div className="ps-clue-label">{c.label}</div>
              <div className="ps-clue-detail">{c.detail}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ===================================================================
// Decision layer — varied prompt + 4 options
// ===================================================================
const DecisionLayer = ({ decision, chosenId, onChoose, locked }) => {
  const optionClass = (opt) => {
    if (!locked) return 'ps-option';
    if (opt.id === decision.correctOption) return 'ps-option ps-option-correct';
    if (opt.id === chosenId) {
      const selectedRisk = decision.options.find((o) => o.id === chosenId)?.risk;
      if (selectedRisk === 'risky') return 'ps-option ps-option-risky';
      if (selectedRisk === 'dangerous') return 'ps-option ps-option-dangerous';
    }
    return 'ps-option';
  };
  return (
    <div className="ps-decision" data-testid="ps-decision">
      <div className="ps-decision-prompt-tag">Decision moment</div>
      <h3 className="ps-decision-prompt">{decision.questionText}</h3>
      <div className="ps-options">
        {decision.options.map((opt) => (
          <button
            key={opt.id}
            className={optionClass(opt)}
            onClick={() => onChoose(opt.id)}
            disabled={locked}
            data-testid={`ps-option-${opt.id}`}
          >
            <span className="ps-option-letter">{opt.id}</span>
            <span className="ps-option-text">{opt.text}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// ===================================================================
// Consequence feedback — three-panel breakdown
// ===================================================================
const ConsequenceFeedback = ({ result, feedback }) => {
  const fb = feedback?.[result];
  if (!fb || !fb.headline) return null;
  const tone = result === 'safe' ? 'safe' : result === 'risky' ? 'risky' : 'dangerous';
  const toneLabel = { safe: 'Safe move', risky: 'Risky move', dangerous: 'High-risk move' }[tone];
  return (
    <div className={`ps-feedback ps-feedback-${tone}`} data-testid="ps-feedback">
      <div className="ps-feedback-tag">{toneLabel}</div>
      <h4 className="ps-feedback-headline">{fb.headline}</h4>
      <div className="ps-feedback-panels">
        {fb.whatWouldHappen && (
          <div className="ps-feedback-panel">
            <div className="ps-feedback-panel-label">What would happen next</div>
            <p>{fb.whatWouldHappen}</p>
          </div>
        )}
        {fb.whatScammerWanted && (
          <div className="ps-feedback-panel">
            <div className="ps-feedback-panel-label">What the scammer wanted</div>
            <p>{fb.whatScammerWanted}</p>
          </div>
        )}
        {fb.whyItWorks && (
          <div className="ps-feedback-panel">
            <div className="ps-feedback-panel-label">Why this works on normal people</div>
            <p>{fb.whyItWorks}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ===================================================================
// Rule unlocked — gold seal
// ===================================================================
const RuleUnlocked = ({ rule }) => {
  if (!rule) return null;
  return (
    <div className="ps-rule" data-testid="ps-rule">
      <div className="ps-rule-seal"><Sparkles size={14} /> Rule unlocked</div>
      <div className="ps-rule-text">{rule}</div>
    </div>
  );
};

// ===================================================================
// Trust footer — source + reviewed date + difficulty/audience
// ===================================================================
const TrustFooter = ({ source, audience, difficulty }) => {
  if (!source) return null;
  const typeLabel = {
    direct_guidance: 'Direct source guidance',
    composite_pattern: 'Composite pattern',
    emerging_trend: 'Emerging trend',
  }[source.type] || 'Source-backed';
  return (
    <div className="ps-trust" data-testid="ps-trust">
      <div className="ps-trust-row">
        <span className="ps-trust-chip ps-trust-chip-source">
          <ShieldCheck size={11} /> {source.primary}
        </span>
        <span className="ps-trust-chip">{typeLabel}</span>
        {source.lastReviewedDate && (
          <span className="ps-trust-chip ps-trust-chip-muted">Reviewed {source.lastReviewedDate}</span>
        )}
        {audience && audience !== 'anyone' && <span className="ps-trust-chip ps-trust-chip-muted">For {audience}s</span>}
        {difficulty && <span className="ps-trust-chip ps-trust-chip-muted">{difficulty}</span>}
      </div>
      <p className="ps-trust-label">{source.label}</p>
    </div>
  );
};

// ===================================================================
// Main scenario renderer — handles single_decision + multi_step
// ===================================================================
const PremiumScenarioCard = ({
  scenario,
  onComplete,
  showTrustFooter = true,
}) => {
  const isMultiStep = scenario.scenarioType === 'multi_step' && Array.isArray(scenario.stages);
  const [stageIdx, setStageIdx] = useState(0);
  const [chosenId, setChosenId] = useState(null);
  const [result, setResult] = useState(null); // 'safe' | 'risky' | 'dangerous'
  const [stageResults, setStageResults] = useState([]); // for multi-step tally
  const [finalDone, setFinalDone] = useState(false);

  const ChannelIcon = CHANNEL_ICONS[scenario.messageType] || MessageSquare;
  const channelLabel = CHANNEL_LABELS[scenario.messageType] || 'Message';

  // Active "stage" for multi-step, or the top-level decision for single_decision
  const activeStage = isMultiStep ? scenario.stages[stageIdx] : null;
  const decision = isMultiStep ? activeStage.decision : scenario.decision;
  const feedback = isMultiStep ? activeStage.feedback : scenario.feedback;
  const activeContent = isMultiStep ? activeStage.scenarioContent : scenario.scenarioContent;

  const choose = (optionId) => {
    if (result) return;
    const opt = decision.options.find((o) => o.id === optionId);
    if (!opt) return;
    setChosenId(optionId);
    setResult(opt.risk);
  };

  const advance = () => {
    const points = { safe: 10, risky: 4, dangerous: 0 }[result];
    if (isMultiStep && stageIdx + 1 < scenario.stages.length) {
      setStageResults([...stageResults, { stageId: activeStage.stageId, result, points }]);
      setStageIdx(stageIdx + 1);
      setChosenId(null);
      setResult(null);
      window.scrollTo({ top: 200, behavior: 'smooth' });
      return;
    }
    setFinalDone(true);
    const finalStageResults = isMultiStep ? [...stageResults, { stageId: activeStage.stageId, result, points }] : null;
    // Bubble up the overall result — for multi_step we take the LOWEST stage as the final risk
    // (a single dangerous in a multi-step still scores as 'dangerous')
    let outcome = result;
    let totalPoints = points;
    if (finalStageResults) {
      const order = { safe: 0, risky: 1, dangerous: 2 };
      outcome = finalStageResults.reduce((acc, r) => order[r.result] > order[acc] ? r.result : acc, 'safe');
      totalPoints = Math.round(finalStageResults.reduce((s, r) => s + r.points, 0) / finalStageResults.length);
    }
    if (onComplete) onComplete({ scenario, result: outcome, points: totalPoints, ruleUnlocked: scenario.ruleUnlocked });
  };

  const stageCount = isMultiStep ? scenario.stages.length : 1;

  return (
    <article className="ps-card" data-testid={`ps-card-${scenario.id}`}>
      {/* Layer 1: Setup / Context */}
      {scenario.scenarioIntro && (
        <header className="ps-intro" data-testid="ps-intro">
          <div className="ps-intro-tagrow">
            <span className="ps-intro-cat">
              <ChannelIcon size={12} /> {channelLabel}
            </span>
            <span className="ps-intro-cat ps-intro-cat-muted">{CATEGORY_LABEL[scenario.category] || scenario.category}</span>
            {isMultiStep && (
              <span className="ps-intro-cat ps-intro-cat-gold">Multi-step · stage {stageIdx + 1} / {stageCount}</span>
            )}
          </div>
          <h2 className="ps-title">{scenario.title}</h2>
          <div className="ps-intro-grid">
            {scenario.scenarioIntro.scene && (
              <div className="ps-intro-row"><Clock size={13} /><p>{scenario.scenarioIntro.scene}</p></div>
            )}
            {scenario.scenarioIntro.context && (
              <div className="ps-intro-row"><Calendar size={13} /><p>{scenario.scenarioIntro.context}</p></div>
            )}
            {scenario.scenarioIntro.primer && (
              <div className="ps-intro-row ps-intro-row-primer"><BookOpen size={13} /><p>{scenario.scenarioIntro.primer}</p></div>
            )}
          </div>
        </header>
      )}

      {/* Multi-step stage intro */}
      {isMultiStep && activeStage.stageIntro && (
        <div className="ps-stage-banner">
          <span className="ps-stage-dot" />
          {activeStage.stageIntro}
        </div>
      )}

      {/* Layer 2: Scam content — channel-realistic */}
      <section className="ps-content-wrap">
        {renderChannel(
          activeContent?.channel || scenario.messageType,
          activeContent?.sender,
          activeContent?.messages,
          activeContent?.transcript,
        )}
      </section>

      {/* Layer 3: Optional clues — only show pre-decision, on first stage */}
      {!result && !isMultiStep && <OptionalClues clues={scenario.optionalClues} />}
      {!result && isMultiStep && stageIdx === 0 && <OptionalClues clues={scenario.optionalClues} />}

      {/* Layer 4: Decision */}
      <DecisionLayer
        decision={decision}
        chosenId={chosenId}
        onChoose={choose}
        locked={!!result}
      />

      {/* Layer 5: Consequence feedback */}
      {result && <ConsequenceFeedback result={result} feedback={feedback} />}

      {/* Mid-scenario advance for multi-step */}
      {result && isMultiStep && stageIdx + 1 < scenario.stages.length && (
        <div className="ps-next-row">
          <button className="ps-btn-primary" onClick={advance} data-testid="ps-next-stage">
            Continue to stage {stageIdx + 2} <ArrowRight size={16} />
          </button>
        </div>
      )}

      {/* Final-stage outputs: Rule + Trust footer + Done CTA */}
      {result && (!isMultiStep || stageIdx + 1 === stageCount) && (
        <>
          <RuleUnlocked rule={scenario.ruleUnlocked} />
          {scenario.safestNextStep && (
            <div className="ps-next-step" data-testid="ps-next-step">
              <div className="ps-next-step-label">Safest next step</div>
              <p>{scenario.safestNextStep}</p>
            </div>
          )}
          {showTrustFooter && (
            <TrustFooter source={scenario.source} audience={scenario.audience} difficulty={scenario.difficulty} />
          )}
          {!finalDone && (
            <div className="ps-next-row">
              <button className="ps-btn-primary" onClick={advance} data-testid="ps-scenario-complete">
                Continue <ArrowRight size={16} />
              </button>
            </div>
          )}
        </>
      )}
    </article>
  );
};

export default PremiumScenarioCard;
